import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  LinearProgress,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Stack
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HourglassEmpty as HourglassEmptyIcon,
  PlayArrow as PlayArrowIcon,
  PeopleAlt as PeopleAltIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { surveyService } from '../services/surveyService';
import { SurveyExecution } from '../types/survey';

interface ExecutionStatusProps {
  executionId: string;
  autoRefresh?: boolean;
  refreshInterval?: number;
}

export const ExecutionStatus: React.FC<ExecutionStatusProps> = ({ 
  executionId, 
  autoRefresh = true, 
  refreshInterval = 5000 
}) => {
  const { token } = useAuth();
  const [execution, setExecution] = useState<SurveyExecution | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = async () => {
    if (!token) return;
    
    try {
      setError(null);
      const data = await surveyService.getExecutionStatus(executionId, token);
      setExecution(data);
      setLoading(false);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch execution status');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, [executionId, token]);

  useEffect(() => {
    if (!autoRefresh || !execution) return;

    // Don't auto-refresh if execution is completed or failed
    if (execution.status === 'completed' || execution.status === 'failed') {
      return;
    }

    const interval = setInterval(fetchStatus, refreshInterval);
    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, execution]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'running':
      case 'in_progress':
        return 'info';
      case 'fetching_panelists':
        return 'secondary';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'error';
      case 'partial':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon />;
      case 'running':
      case 'in_progress':
        return <PlayArrowIcon />;
      case 'fetching_panelists':
        return <PeopleAltIcon />;
      case 'pending':
        return <HourglassEmptyIcon />;
      case 'failed':
      case 'partial':
        return <ErrorIcon />;
      default:
        return undefined;
    }
  };

  const formatStatusLabel = (status: string) => {
    return status.toUpperCase().replace(/_/g, ' ');
  };

  if (loading) {
    return (
      <Paper elevation={2} sx={{ p: 3 }}>
        <LinearProgress />
        <Typography variant="body2" sx={{ mt: 2 }} color="text.secondary">
          Loading execution status...
        </Typography>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper elevation={2} sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Paper>
    );
  }

  if (!execution) {
    return null;
  }

  const progress = execution.target_completes > 0
    ? (execution.completed_completes / execution.target_completes) * 100
    : 0;

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" color="primary" fontWeight={600}>
          Execution Status
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Chip
            label={formatStatusLabel(execution.status)}
            color={getStatusColor(execution.status) as any}
            icon={getStatusIcon(execution.status) || undefined}
            size="small"
          />
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={fetchStatus}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
        <Box>
          <Typography variant="body2" color="text.secondary">
            Execution ID
          </Typography>
          <Typography variant="body1" fontWeight={500} sx={{ wordBreak: 'break-all' }}>
            {execution.execution_id}
          </Typography>
        </Box>
        <Box>
          <Typography variant="body2" color="text.secondary">
            Survey ID
          </Typography>
          <Typography variant="body1" fontWeight={500}>
            {execution.survey_id}
          </Typography>
        </Box>
        <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
          <Typography variant="body2" color="text.secondary">
            Survey URL
          </Typography>
          <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
            {execution.survey_url}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ mt: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" fontWeight={500}>
            Progress
          </Typography>
          <Typography variant="body2" fontWeight={500}>
            {execution.completed_completes} / {execution.target_completes}
          </Typography>
        </Box>
        <LinearProgress 
          variant="determinate" 
          value={progress} 
          sx={{ height: 10, borderRadius: 1 }}
          color={getStatusColor(execution.status) as any}
        />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 2, mt: 2 }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h5" color="success.main" fontWeight={600}>
            {execution.completed_completes}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Completed
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h5" color="warning.main" fontWeight={600}>
            {Math.max(0, execution.target_completes - execution.completed_completes - execution.in_progress_completes - execution.failed_completes)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Pending
          </Typography>
        </Box>
        <Tooltip
          title={
            execution.in_progress_details && execution.in_progress_details.length > 0 ? (
              <Box sx={{ p: 1, minWidth: 320 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  In-Progress Panelists
                </Typography>
                <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                  <Box 
                    component="table" 
                    sx={{ 
                      width: '100%', 
                      borderCollapse: 'collapse',
                      '& th, & td': { 
                        px: 1, 
                        py: 0.5, 
                        textAlign: 'left',
                        fontSize: '0.75rem',
                        whiteSpace: 'nowrap'
                      },
                      '& th': { 
                        fontWeight: 600,
                        borderBottom: '1px solid rgba(255,255,255,0.3)'
                      }
                    }}
                  >
                    <Box component="thead">
                      <Box component="tr">
                        <Box component="th">Name</Box>
                        <Box component="th">Question</Box>
                        <Box component="th">Progress</Box>
                      </Box>
                    </Box>
                    <Box component="tbody">
                      {execution.in_progress_details.map((detail) => (
                        <Box component="tr" key={detail.panelist_id}>
                          <Box component="td">{detail.panelist_name}</Box>
                          <Box component="td">
                            {detail.current_question_id || `Page ${detail.pages_completed + 1}`}
                          </Box>
                          <Box component="td">
                            {detail.survey_progress_percent != null 
                              ? `${detail.survey_progress_percent.toFixed(0)}%` 
                              : '-'}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
              </Box>
            ) : (
              execution.in_progress_completes > 0 
                ? 'No details available' 
                : 'No panelists in progress'
            )
          }
          arrow
          placement="top"
          componentsProps={{
            tooltip: {
              sx: {
                maxWidth: 500,
                minWidth: 400,
                bgcolor: 'rgba(50, 50, 50, 0.95)'
              }
            }
          }}
        >
          <Box sx={{ textAlign: 'center', cursor: 'pointer' }}>
            <Typography variant="h5" color="info.main" fontWeight={600}>
              {execution.in_progress_completes}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              In Progress
            </Typography>
          </Box>
        </Tooltip>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h5" color="error.main" fontWeight={600}>
            {execution.failed_completes}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Failed
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 2 }}>
        <Box>
          <Typography variant="body2" color="text.secondary">
            Created
          </Typography>
          <Typography variant="body2">
            {new Date(execution.created_at).toLocaleString()}
          </Typography>
        </Box>
        <Box>
          <Typography variant="body2" color="text.secondary">
            Last Updated
          </Typography>
          <Typography variant="body2">
            {new Date(execution.updated_at).toLocaleString()}
          </Typography>
        </Box>
        {execution.completed_at && (
          <Box sx={{ gridColumn: '1 / -1' }}>
            <Typography variant="body2" color="text.secondary">
              Completed
            </Typography>
            <Typography variant="body2">
              {new Date(execution.completed_at).toLocaleString()}
            </Typography>
          </Box>
        )}
      </Box>

      {execution.error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {execution.error}
        </Alert>
      )}
    </Paper>
  );
};

export default ExecutionStatus;

